import React, { useEffect, useState } from "react";
import { Box, Container, Typography, CircularProgress, Alert } from "@mui/material";
import PsychologistApi from "../api/PsychologistApi";

// Leesbare weergave van de samenwerkingsovereenkomst voor ingelogde
// psychologen (/psycholoog-voorwaarden). Bron + versie komen van de API
// (data/providerTerms.ts); het klik-akkoord gebeurt in de onboarding-wizard.
// Eenvoudige markdown-weergave zonder externe renderer: koppen, blockquotes
// en paragrafen — voldoende voor een juridische tekst.
function renderMd(md: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let quote: string[] = [];
  const flushQuote = (key: string) => {
    if (!quote.length) return;
    out.push(
      <Alert key={key} severity="info" sx={{ my: 2 }}>{quote.join(" ")}</Alert>
    );
    quote = [];
  };
  md.split(/\n\n+/).forEach((block, i) => {
    const b = block.trim();
    if (!b) return;
    if (b.startsWith(">")) {
      quote.push(b.replace(/^>\s?/gm, "").replace(/\n/g, " "));
      return;
    }
    flushQuote(`q${i}`);
    if (b.startsWith("## ")) {
      out.push(<Typography key={i} variant="h2" sx={{ fontSize: "1.25rem", mt: 4, mb: 1 }}>{b.slice(3)}</Typography>);
    } else if (b.startsWith("# ")) {
      out.push(<Typography key={i} variant="h1" sx={{ fontSize: { xs: "1.7rem", sm: "2.1rem" }, mb: 2 }}>{b.slice(2)}</Typography>);
    } else {
      // Vet (**…**) omzetten; rest als platte tekst laten staan.
      const parts = b.split(/\*\*(.+?)\*\*/g);
      out.push(
        <Typography key={i} sx={{ mb: 1.5, color: "text.secondary", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
          {parts.map((p, j) => (j % 2 === 1 ? <strong key={j}>{p}</strong> : p))}
        </Typography>
      );
    }
  });
  flushQuote("qend");
  return out;
}

export default function ProviderTerms() {
  const [terms, setTerms] = useState<{ version: string; markdown: string } | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    PsychologistApi.getTerms().then(setTerms).catch(() => setError(true));
  }, []);

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="warning">Deze pagina is alleen beschikbaar voor ingelogde psychologen.</Alert>
      </Container>
    );
  }
  if (!terms) return <Box sx={{ minHeight: "40dvh", display: "grid", placeItems: "center" }}><CircularProgress sx={{ color: "primary.main" }} /></Box>;

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      <Container maxWidth="md" sx={{ py: { xs: 5, sm: 8 } }}>
        {renderMd(terms.markdown)}
        <Typography sx={{ mt: 4, fontSize: ".85rem", color: "text.secondary" }}>Versie: {terms.version}</Typography>
      </Container>
    </Box>
  );
}
